import React from "react";
import Panel from "../../../components/panels/panel";
import { usePagination } from "../../../utilities/hookutils";
import AddNewModal from "./AddNewModal";
import ContactWithWorkRelationRowCard from "./ContactRowCard";
import { useShortcutPanel } from "components/Shortcuts/hooks";
import { ButtonSecondary } from "components/Atoms/Button";
import { useDebounce } from "utilities/usehooks";
import { NoDataAvailableDiv } from "components/NoDataAvailableDiv";
import { SearchInput } from "components/Atoms/SearchInput";

interface Props {
  companyId: number
  panelConfig: server.dto.LinkedContactPanelConfig
}

const LinkedContacts = (props: Props) => {
  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const [hasMoreThan10Contacts, setHasMoreThan10Contacts] = React.useState<boolean>(false);
  const [company, setCompany] = React.useState<server.dto.Company>(null);
  const [contacts, setContacts] = React.useState<server.dto.ContactWithWorkRelation[]>([]);
  const [isInitialLoading, setIsInitialLoading] = React.useState(true);
  const [displayNewContactModal, setDisplayNewContactModal] = React.useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery);
  const perPage = 10;

  const paginate = async (paginationData: Partial<server.dto.PaginationData>) => {
    const instruction: server.dto.ListContactByCompanyInstruction = {
      keyword: debouncedSearchQuery,
      page: paginationData.page,
      perPage,
      companyId: props.companyId
    };

    const res = await window.CoreApi.CompanyToContact.ListContactByCompany(instruction);

    setContacts(res.data.data);
    setIsInitialLoading(false);

    if (isInitialLoading) {
      if (res.data.pagination.total > 10) {
        setHasMoreThan10Contacts(true);
      }
    }

    return res.data.pagination;
  };

  const pagination = usePagination(perPage, paginate, {
    hasResults: false,
    maxPerPage: perPage,
    page: 1,
    pageCount: 1,
    perPage,
    total: 0
  });

  React.useEffect(() => {
    window.CoreApi.Company.GetPreviewPanelData(props.companyId)
      .then(res => {
        setCompany(res.data.data.company);
      });
  }, []);

  React.useEffect(() => {
    if (company != null) {
      pagination.onPaginate(1);
    }
  }, [company, debouncedSearchQuery]);

  const onContactEdited = () => {
    pagination.onPaginate(pagination.page);
  };

  const onAddNewContactClosed = (anythingChanged: boolean) => {
    setDisplayNewContactModal(false);
    if (anythingChanged) {
      pagination.onPaginate(pagination.page);
    }
  };

  const { refToScrollTo } = useShortcutPanel({
    type: "contact",
    label: props.panelConfig.headerLabel,
    totalItems: pagination.total,
    onAddClicked: props.panelConfig.canAdd ? () => { setDisplayNewContactModal(true); } : null

  });

  return <>
    {displayNewContactModal && <AddNewModal
      company={company}
      onClose={onAddNewContactClosed}
      excludeKeys={contacts.map(c => c.contact.contactId)}
    />}

    <Panel
      panelClassName="s1_related-feature-panel"
      panelRef={refToScrollTo}
      title={props.panelConfig.headerLabel}
      isLoading={isInitialLoading}
      pagination={pagination.Component}
      button={props.panelConfig.canAdd
        ? <ButtonSecondary
          onClick={() => { setDisplayNewContactModal(true); }}
          label={RESX.GeneralLabels.resxNew} />
        : null
      }
    >
      {hasMoreThan10Contacts && <SearchInput
        onChange={(_, { value }) => { setSearchQuery(value); }}
        value={searchQuery}
        placeholder={RESX.Company.SearchByName}
      />}
      {contacts.length > 0 ? <div className="list-with-gaps line-layout">
        <div className="list-with-gaps-body">
          {contacts.map(contactWithWorkRelation =>
            <ContactWithWorkRelationRowCard
              key={contactWithWorkRelation.contact.contactId}
              company={company}
              contactWithWorkRelation={contactWithWorkRelation}
              onContactEdited={onContactEdited}
            />
          )}
        </div>
      </div> : <>
        <NoDataAvailableDiv />
      </>}
    </Panel>
  </>;
};

export default LinkedContacts;